import type { DetailTask, InboxTasksRequest, ListTask } from '../sources';
import type { TabType } from '../TaskDataManager';

export interface State {
    inboxTasks: ListTask[];
    isLoadingInbox: boolean;
    currentRequest: InboxTasksRequest & { total?: number };
    selectedTask: DetailTask | null;
    isLoadingSelectedTask: boolean;
    currentUserEmail: string | null;
}

export type Action =
    | { type: 'SET_LOADING_INBOX'; isLoading: boolean }
    | {
          type: 'SET_INBOX_TASKS';
          tasks: ListTask[];
          request: InboxTasksRequest & { total?: number };
      }
    | {
          type: 'APPEND_INBOX_TASKS';
          tasks: ListTask[];
          request: InboxTasksRequest & { total?: number };
      }
    | { type: 'REALTIME_ADD_TASK'; task: ListTask; currentTab: TabType }
    | { type: 'REALTIME_REMOVE_TASK'; taskId: string }
    | { type: 'CLEAR_NEWLY_ADDED_FLAG'; taskId: string }
    | { type: 'COMPLETE_REMOVE_ANIMATION'; taskId: string }
    | { type: 'SET_LOADING_SELECTED_TASK'; isLoading: boolean }
    | { type: 'SET_SELECTED_TASK'; task: DetailTask | null };

export const initialState: State = {
    inboxTasks: [],
    isLoadingInbox: false,
    currentRequest: {},
    selectedTask: null,
    isLoadingSelectedTask: false,
    currentUserEmail: null,
};

export const taskReducer = (state: State, action: Action): State => {
    switch (action.type) {
        case 'SET_LOADING_INBOX':
            return {
                ...state,
                isLoadingInbox: action.isLoading,
            };

        case 'SET_INBOX_TASKS':
            return {
                ...state,
                inboxTasks: action.tasks,
                currentRequest: action.request,
            };

        case 'APPEND_INBOX_TASKS':
            return {
                ...state,
                inboxTasks: [...state.inboxTasks, ...action.tasks].filter(
                    (task, index, arr) => arr.findIndex((t) => t.id === task.id) === index,
                ),
                currentRequest: action.request,
            };

        case 'REALTIME_ADD_TASK': {
            const { task, currentTab } = action;

            if (
                state.currentRequest.status &&
                state.currentRequest.status !== 'Any' &&
                state.currentRequest.status !== task.status
            ) {
                // Status not in current request filter list, don't add task
                return state;
            }

            const isMyTask = task.createdBy === state.currentUserEmail;
            const isAssignedToMe = task.canApprove;
            const isMyTasksTab = currentTab === 'watching';
            if ((isMyTasksTab && !isMyTask) || (!isMyTasksTab && !isAssignedToMe)) {
                // task is in other tab, don't add task
                return state;
            }

            if (state.inboxTasks.some((t) => t.id === task.id)) {
                // task already exists, maintain isNewlyAdded flag, update all other fields
                return {
                    ...state,
                    inboxTasks: state.inboxTasks.map((t) =>
                        t.id === task.id
                            ? { ...task, ...(t.isNewlyAdded && { isNewlyAdded: t.isNewlyAdded }) }
                            : t,
                    ),
                };
            }

            // task doesn't exist, add it to the list
            const newTask: ListTask = { ...task, isNewlyAdded: true };

            return {
                ...state,
                inboxTasks: [newTask, ...(state.inboxTasks ?? [])],
                currentRequest: state.currentRequest.total
                    ? {
                          ...state.currentRequest,
                          total: state.currentRequest.total + 1,
                      }
                    : state.currentRequest,
            };
        }

        case 'REALTIME_REMOVE_TASK': {
            if (!state.inboxTasks.some((task) => task.id === action.taskId)) {
                // task doesn't exist, nothing to remove
                return state;
            }

            const updatedTasks = state.inboxTasks.map((task) =>
                task.id === action.taskId
                    ? { ...task, isRemoving: true, isNewlyAdded: false }
                    : task,
            );

            return {
                ...state,
                inboxTasks: updatedTasks,
                selectedTask: state.selectedTask?.id === action.taskId ? null : state.selectedTask,
                currentRequest:
                    state.currentRequest.total && state.currentRequest.total > 0
                        ? {
                              ...state.currentRequest,
                              total: state.currentRequest.total - 1,
                          }
                        : state.currentRequest,
            };
        }

        case 'CLEAR_NEWLY_ADDED_FLAG': {
            if (!state.inboxTasks) {
                return state;
            }

            const updatedTasks = state.inboxTasks.map((task) =>
                task.id === action.taskId ? { ...task, isNewlyAdded: false } : task,
            );

            return {
                ...state,
                inboxTasks: updatedTasks,
            };
        }

        case 'COMPLETE_REMOVE_ANIMATION': {
            if (!state.inboxTasks) {
                return state;
            }

            const updatedTasks = state.inboxTasks.filter((task) => task.id !== action.taskId);

            return {
                ...state,
                inboxTasks: updatedTasks,
            };
        }

        case 'SET_LOADING_SELECTED_TASK':
            return {
                ...state,
                isLoadingSelectedTask: action.isLoading,
            };

        case 'SET_SELECTED_TASK':
            return {
                ...state,
                selectedTask: action.task,
            };

        default:
            return state;
    }
};
